import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Text,
    Center,
    Loader,
    Stack,
    Flex,
    TextInput,
    Button,
    Title,
    Pagination,
    Paper,
    Group,
    Badge,
    Select,
    Collapse,
} from "@mantine/core";
import {
    IconSearch,
    IconSortAscending,
    IconSortDescending,
    IconChevronDown,
    IconChevronUp,
} from "@tabler/icons-react";
import { Shift } from "../../types/Shift";
import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/forum.png";
import { getFunctions, type FunctionItem } from "../../api/shiftsApi";

interface Props {
    eventId: number;
    language: string;
}

const AllAvailableShiftListPublic: React.FC<Props> = ({ eventId, language }) => {
    const apiUrl = process.env.REACT_APP_API_URL as string;
    const navigate = useNavigate();

    const [shifts, setShifts] = useState<Shift[]>([]);
    const [functions, setFunctions] = useState<FunctionItem[]>([]);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState("");
    const [sortAsc, setSortAsc] = useState(true);
    const [activePage, setActivePage] = useState(1);

    const [selectedFunctionId, setSelectedFunctionId] = useState<number | "all">("all");
    const [functionSearch, setFunctionSearch] = useState("");

    const [openDescriptions, setOpenDescriptions] = useState<Record<number, boolean>>({});

    const ITEMS_PER_PAGE = 25;

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const shiftsRes = await fetch(`${apiUrl}/api/events/${eventId}/shifts/available`);
                const shiftsData = await shiftsRes.json();
                const safeShifts = Array.isArray(shiftsData) ? shiftsData : shiftsData?.$values ?? [];
                setShifts(safeShifts);

                const functionsData = await getFunctions(apiUrl, eventId);
                const safeFunctions = Array.isArray(functionsData)
                    ? functionsData
                    : (functionsData as any)?.$values ?? [];

                setFunctions(safeFunctions);
            } catch (error) {
                console.error("Failed to fetch shifts/functions:", error);
                setShifts([]);
                setFunctions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [apiUrl, eventId]);

    useEffect(() => {
        setActivePage(1);
    }, [searchTerm, sortAsc, selectedFunctionId]);

    const functionSelectData = useMemo(() => {
        const mapped = functions
            .filter((f) => f?.functionId != null && f.functionCategoryId === 10)
            .map((f) => ({
                value: String(f.functionId),
                label: f.functionName?.trim() || `Funktion ${f.functionId}`,
            }));

        return [
            {
                value: "all",
                label: language === "da" ? "Alle funktioner" : "All functions",
            },
            ...mapped,
        ];
    }, [functions, language]);

    const functionDescriptionById = useMemo(() => {
        const map = new Map<number, string>();

        functions
            .filter((f) => f?.functionId != null && f.functionCategoryId === 10)
            .forEach((f) => {
                map.set(f.functionId, f.description ?? "");
            });

        return map;
    }, [functions]);

    const functionDescriptionByName = useMemo(() => {
        const map = new Map<string, string>();

        functions
            .filter((f) => f?.functionId != null && f.functionCategoryId === 10)
            .forEach((f) => {
                const key = (f.functionName ?? "").trim().toLowerCase();
                if (key) map.set(key, f.description ?? "");
            });

        return map;
    }, [functions]);

    const filteredShifts = useMemo(() => {
        const safeShifts = Array.isArray(shifts) ? shifts : [];
        const term = searchTerm.trim().toLowerCase();

        return safeShifts
            .filter((s: any) => {
                const startDate = new Date(s.startTime);

                const nameStr = (s.shiftsName ?? "").toLowerCase();
                const functionStr = (s.functionName ?? "").toLowerCase();

                const dayStr = startDate
                    .toLocaleDateString("da-DK", { weekday: "long" })
                    .toLowerCase();

                const dateStr = startDate.toLocaleDateString("da-DK");
                const dateStrPadded = startDate.toLocaleDateString("da-DK", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                });

                const dayOnly = startDate.toLocaleDateString("da-DK", {
                    day: "2-digit",
                });

                const monthOnly = startDate.toLocaleDateString("da-DK", {
                    month: "2-digit",
                });

                const normalizedTerm = term
                    .replace(/\s/g, "")
                    .replace(/-/g, ".")
                    .replace(/\//g, ".");
                const normalizedDate = dateStr.replace(/\s/g, "");
                const normalizedDatePadded = dateStrPadded.replace(/\s/g, "");

                const matchesSearch =
                    term === "" ||
                    nameStr.includes(term) ||
                    functionStr.includes(term) ||
                    dayStr.includes(term) ||
                    normalizedDate.includes(normalizedTerm) ||
                    normalizedDatePadded.includes(normalizedTerm) ||
                    dayOnly.includes(normalizedTerm) ||
                    monthOnly.includes(normalizedTerm);

                const shiftFunctionId = s.functionID ?? s.functionId ?? null;

                const matchesFunction =
                    selectedFunctionId === "all" ||
                    (shiftFunctionId != null && Number(shiftFunctionId) === selectedFunctionId);

                return matchesSearch && matchesFunction;
            })
            .sort((a, b) => {
                const dateA = new Date(a.startTime).getTime();
                const dateB = new Date(b.startTime).getTime();
                return sortAsc ? dateA - dateB : dateB - dateA;
            });
    }, [shifts, searchTerm, selectedFunctionId, sortAsc]);

    const totalPages = Math.ceil(filteredShifts.length / ITEMS_PER_PAGE);

    const paginatedShifts = filteredShifts.slice(
        (activePage - 1) * ITEMS_PER_PAGE,
        activePage * ITEMS_PER_PAGE
    );

    const handleGoToLogin = (shiftId: number) => {
        navigate(`/login?eventId=${eventId}&shiftId=${shiftId}`);
    };

    const toggleDescription = (shiftId: number) => {
        setOpenDescriptions((prev) => ({
            ...prev,
            [shiftId]: !prev[shiftId],
        }));
    };

    const getFunctionDescription = (shift: any) => {
        const shiftFunctionId = shift.functionID ?? shift.functionId ?? null;

        if (shiftFunctionId != null) {
            const desc = functionDescriptionById.get(Number(shiftFunctionId));
            if (desc) return desc;
        }

        const functionNameKey = (shift.functionName ?? "").trim().toLowerCase();
        if (functionNameKey) {
            return functionDescriptionByName.get(functionNameKey) ?? "";
        }

        return "";
    };

    if (loading) {
        return (
            <Center mt="md">
                <Loader color="red" />
            </Center>
        );
    }

    return (
        <div style={{ width: "100%", margin: "0 auto" }}>
            <HeroSectionV2
                titleEn="Shifts"
                titleDa="Vagter"
                descriptionEn="All available shifts"
                descriptionDa="Alle ledige vagter"
                imageSrc={Billede1}
                badgeText=""
                logoSrc=""
            />

            <div style={{ width: "60%", margin: "0 auto" }}>
                <Stack>
                    <Center my="md" style={{ flexDirection: "column", gap: 8 }}>
                        <Title order={2}>
                            {language === "da" ? "Alle ledige vagter" : "All available shifts"}
                        </Title>

                        <Text size="sm" c="dimmed">
                            {language === "da"
                                ? "Du kan se vagterne her, men skal logge ind for at vælge en vagt."
                                : "You can view shifts here, but you need to log in to choose a shift."}
                        </Text>

                        <Button
                            variant="light"
                            size="xs"
                            color="gray"
                            onClick={() => navigate(`/`)}
                        >
                            {language === "da"
                                ? "← Tilbage til funktioner"
                                : "← Back to functions"}
                        </Button>
                    </Center>

                    <Flex gap="sm" justify="center" align="flex-end" wrap="wrap" mt="md">
                        <TextInput
                            style={{ flex: 1, minWidth: "240px", maxWidth: "400px" }}
                            label={language === "da" ? "Søg vagt eller dato" : "Search shift or date"}
                            placeholder={
                                language === "da" ? "f.eks. Fredag eller 17.04" : "e.g. Friday or 17.04"
                            }
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.currentTarget.value)}
                            leftSection={<IconSearch size={16} />}
                        />

                        <Select
                            style={{ minWidth: "260px" }}
                            label={language === "da" ? "Funktion" : "Function"}
                            placeholder={
                                language === "da" ? "Søg funktion..." : "Search function..."
                            }
                            data={functionSelectData}
                            value={selectedFunctionId === "all" ? "all" : String(selectedFunctionId)}
                            onChange={(v) => {
                                if (!v || v === "all") setSelectedFunctionId("all");
                                else setSelectedFunctionId(Number(v));
                            }}
                            searchable
                            clearable
                            nothingFoundMessage={
                                language === "da"
                                    ? "Ingen funktioner fundet"
                                    : "No functions found"
                            }
                            maxDropdownHeight={250}
                            searchValue={functionSearch}
                            onSearchChange={setFunctionSearch}
                            onFocus={() => setFunctionSearch("")}
                        />

                        <Button
                            variant="light"
                            onClick={() => setSortAsc(!sortAsc)}
                            leftSection={
                                sortAsc ? (
                                    <IconSortAscending size={16} />
                                ) : (
                                    <IconSortDescending size={16} />
                                )
                            }
                            style={{ height: "40px" }}
                        >
                            {language === "da" ? "Sorter dato" : "Sort date"}
                        </Button>
                    </Flex>

                    {filteredShifts.length === 0 ? (
                        <Text>{language === "da" ? "Ingen vagter fundet." : "No shifts found."}</Text>
                    ) : (
                        <>
                            <Title order={4}>{language === "da" ? "Vagter" : "Shifts"}</Title>

                            <Text size="sm" c="dimmed">
                                {language === "da"
                                    ? `Viser ${(activePage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(
                                        activePage * ITEMS_PER_PAGE,
                                        filteredShifts.length
                                    )} af ${filteredShifts.length} vagter`
                                    : `Showing ${(activePage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(
                                        activePage * ITEMS_PER_PAGE,
                                        filteredShifts.length
                                    )} of ${filteredShifts.length} shifts`}
                            </Text>

                            <Stack gap="sm">
                                {paginatedShifts.map((shift: any) => {
                                    const start = new Date(shift.startTime);
                                    const end = new Date(shift.endTime);
                                    const description = getFunctionDescription(shift);
                                    const isOpen = !!openDescriptions[shift.shiftsID];

                                    return (
                                        <Paper key={shift.shiftsID} withBorder p="md" radius="md">
                                            <Group justify="space-between" align="flex-start" wrap="wrap">
                                                <Stack gap={4} style={{ flex: 1 }}>
                                                    <Text fw={600}>{shift.shiftsName}</Text>

                                                    {shift.functionName && (
                                                        <Text size="sm" c="dimmed">
                                                            {shift.functionName}
                                                        </Text>
                                                    )}

                                                    <Text size="sm">
                                                        {start.toLocaleDateString("da-DK", {
                                                            weekday: "long",
                                                            day: "2-digit",
                                                            month: "2-digit",
                                                            year: "numeric",
                                                        })}
                                                    </Text>

                                                    <Text size="sm">
                                                        {start.toLocaleTimeString("da-DK", {
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        })}
                                                        {" - "}
                                                        {end.toLocaleTimeString("da-DK", {
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        })}
                                                    </Text>

                                                    <Badge variant="light">
                                                        {language === "da"
                                                            ? `${shift.availableSpots ?? 0} ledige pladser`
                                                            : `${shift.availableSpots ?? 0} available spots`}
                                                    </Badge>

                                                    {!!description && (
                                                        <>
                                                            <Button
                                                                variant="subtle"
                                                                size="xs"
                                                                style={{ alignSelf: "flex-start", paddingLeft: 0 }}
                                                                onClick={() => toggleDescription(shift.shiftsID)}
                                                                rightSection={
                                                                    isOpen ? (
                                                                        <IconChevronUp size={14} />
                                                                    ) : (
                                                                        <IconChevronDown size={14} />
                                                                    )
                                                                }
                                                            >
                                                                {language === "da"
                                                                    ? "Læs mere om funktionen"
                                                                    : "Read more about the function"}
                                                            </Button>

                                                            <Collapse in={isOpen}>
                                                                <Paper
                                                                    mt="xs"
                                                                    p="sm"
                                                                    radius="sm"
                                                                    bg="gray.0"
                                                                >
                                                                    <Text size="sm">{description}</Text>
                                                                </Paper>
                                                            </Collapse>
                                                        </>
                                                    )}
                                                </Stack>

                                                <Button onClick={() => handleGoToLogin(shift.shiftsID)}>
                                                    {language === "da"
                                                        ? "Login for at vælge"
                                                        : "Login to choose"}
                                                </Button>
                                            </Group>
                                        </Paper>
                                    );
                                })}
                            </Stack>

                            {totalPages > 1 && (
                                <Center mt="md">
                                    <Pagination
                                        value={activePage}
                                        onChange={setActivePage}
                                        total={totalPages}
                                    />
                                </Center>
                            )}
                        </>
                    )}
                </Stack>
            </div>
        </div>
    );
};

export default AllAvailableShiftListPublic;